import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';

import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Grid,
  Typography,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { Box, IconButton, InputAdornment } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { COLOR_PRIMARY, DARK_GREY, RoutesMap, secondUserDescriptionMap } from 'src/const';
import { callApi } from 'src/core/callApi';
import Copyright from './Copyright';
import { RegisterPropTypes } from 'src/interfaces/iproptypes';
import { makeLowerCase, getMarketingTrackers } from 'src/utils/functions';
import { timeZoneCCMap } from 'src/App/assets/timeZoneCCMap';

import { GoogleAuth } from '../GoogleAuth';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    overflowY: 'auto',
  },
  image: {
    backgroundImage: 'url(https://airbtics-resource.s3.us-east-2.amazonaws.com/quote.png)',
    backgroundRepeat: 'no-repeat',
    backgroundColor:
      theme.palette.type === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxHeight: '100vh',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: COLOR_PRIMARY,
    '&:hover': {
      backgroundColor: COLOR_PRIMARY,
    },
  },
  progressBar: {
    width: '100%',
  },
  formControl: {
    marginTop: 15,
  },
}));

const userDescriptionMap = ['property_investor', 'property_manager', 'tourism', 'unclassified'];
const setMarketingTrackingURL =
  'https://4b5jgzfm05.execute-api.us-east-2.amazonaws.com/default/setMarketingTracking';

const Login = ({
  registerThunk,
  getTokenThunk,
  googleAuthRegisterThunk,
  loading,
  error,
  errorReason,
  redirectToLoginPage,
  getUserInfo,
  landing,
  device,
}: RegisterPropTypes) => {
  const [lang, page, type, region] = getMarketingTrackers();
  const classes = useStyles();
  const history = useHistory();
  const isLoggedIn = Boolean(getTokenThunk());

  const [email, changeEmail] = useState<string>('');
  const [password, changePassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [name, changeName] = useState<string>('');
  const [userDescription, changeUserDescription] = useState<string>('');
  const [userSubDescription, setUserSubDescription] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const signupCountryCode = timeZoneCCMap[userTimeZone];
  const tailoredRegion = window.localStorage.getItem('tailoredRegion');
  const affiliateKey = window.localStorage.getItem('affiliatedKey');

  let urlToRedirect = window.location.pathname;

  if (
    urlToRedirect.includes(
      '/airbnb-data/united-kingdom/0/the%20city%20of%20brighton%20and%20hove'
    ) ||
    // urlToRedirect.includes('/airbnb-calculator') ||
    urlToRedirect.includes('/discovery')
  ) {
    urlToRedirect = '/airbnb-data?searchPage=true';
  }

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      changeEmail(makeLowerCase(event.target.value));
    },
    []
  );

  React.useEffect(() => {
    console.log('Error Reason ====:::: ', errorReason);
  }, [errorReason]);

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      changePassword(event.target.value);
    },
    []
  );

  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      changeName(event.target.value);
    },
    []
  );

  const handleUserDescriptionChange = useCallback(event => {
    changeUserDescription(event.target.value);
  }, []);

  const handleUserSubDescriptionChange = event => {
    setUserSubDescription(event.target.value);
  };

  console.log('Device: ', device);

  const handleRegister = useCallback(
    async event => {
      event.preventDefault();
      const search_attempt_history = localStorage.getItem('investment-search-attempts');
      const noOfUserSearch = search_attempt_history ? parseInt(search_attempt_history) : 0;

      const successfullRegistration = await registerThunk({
        email,
        password,
        name,
        userDescription,
        userSubDescription,
        lang,
        page,
        type,
        region,
        landing,
        device,
        signupCountryCode,
        tailoredRegion,
        affiliateKey,
        noOfUserSearch,
      });
      if (successfullRegistration) {
        callApi(
          setMarketingTrackingURL,
          {
            queryParams: {
              user_signed_up: true,
            },
          },
          false
        );
        localStorage.removeItem('marketing-trackers');
        const email_verify_url =
          'https://nucx2ebwi6.execute-api.us-east-2.amazonaws.com/default/verifyUser';
        const config = {
          queryParams: {
            action: 'generate_magic_link',
            email: encodeURIComponent(email),
          },
        };
        await callApi(email_verify_url, config, false);
        history.push(
          userDescription === 'property_manager'
            ? RoutesMap.Pricing
            : urlToRedirect
            ? urlToRedirect
            : RoutesMap.Home
        );
        getUserInfo();
      }
    },
    [registerThunk, email, password, name, userDescription, userSubDescription, signupCountryCode]
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  if (isLoggedIn) {
    window.localStorage.setItem('user-name', name);
    window.localStorage.setItem('user-email', email);
    window.location.href = urlToRedirect ? urlToRedirect : '/airbnb-data?searchPage=true';
    // return <Redirect to={urlToRedirect ? urlToRedirect : RoutesMap.Home} />;
  }

  return (
    <>
      {loading && <LinearProgress className={classes.progressBar} />}
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign up
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          {!error ? (
            <Typography style={{ fontSize: '18px', color: DARK_GREY }}>
              Already have an account?
            </Typography>
          ) : errorReason == 'User already exists' ? (
            <Alert severity="error">Looks like you already have an account with this email!</Alert>
          ) : (
            <Alert severity="error">Opps, internal server error. Please try again.</Alert>
          )}
          <Button
            variant="contained"
            style={{
              textTransform: 'none',
              backgroundColor: COLOR_PRIMARY,
              color: '#ffff',
              padding: '2px 5px',
            }}
            onClick={redirectToLoginPage}
          >
            <Typography variant="body1">Login</Typography>
          </Button>
        </Box>

        <GoogleAuth landing={landing} device={device} urlToRedirect={urlToRedirect} />

        <form className={classes.form} noValidate onSubmit={handleRegister}>
          <TextField
            onChange={handleNameChange}
            value={name}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="name"
            label="Your Name"
            name="name"
            autoComplete="name"
          />
          <TextField
            onChange={handleEmailChange}
            value={email}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
          />
          <TextField
            onChange={handlePasswordChange}
            value={password}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(prev => !prev)}
                  >
                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="outlined" fullWidth required={true} className={classes.formControl}>
            <InputLabel id="demo-simple-select-outlined-label">
              How would you best describe yourself?
            </InputLabel>
            <Select
              labelId="demo-simple-select-outlined-label"
              id="demo-simple-select-outlined"
              variant="outlined"
              required
              fullWidth
              label="How would you best describe yourself?"
              open={open}
              onClose={handleClose}
              onOpen={handleOpen}
              value={userDescription}
              onChange={handleUserDescriptionChange}
            >
              <MenuItem value={userDescriptionMap[0]}>Property Investor</MenuItem>
              <MenuItem value={userDescriptionMap[1]}>Airbnb Host / Property Manager</MenuItem>
              <MenuItem value={userDescriptionMap[2]}>Researcher / Government</MenuItem>
              <MenuItem value={userDescriptionMap[3]}>Others</MenuItem>
            </Select>
          </FormControl>

          {secondUserDescriptionMap[userDescription] && (
            <FormControl
              variant="outlined"
              fullWidth
              required={true}
              className={classes.formControl}
            >
              <InputLabel id="demo-simple-select-outlined-label">
                {secondUserDescriptionMap[userDescription]
                  ? Object.keys(secondUserDescriptionMap[userDescription])[0]
                  : ''}
              </InputLabel>
              <Select
                labelId="demo-simple-select-outlined-label"
                id="sub-user-description"
                variant="outlined"
                required
                fullWidth
                label={
                  secondUserDescriptionMap[userDescription]
                    ? Object.keys(secondUserDescriptionMap[userDescription])[0]
                    : ''
                }
                onChange={handleUserSubDescriptionChange}
              >
                {secondUserDescriptionMap[userDescription] &&
                  (Object as any)
                    .entries(secondUserDescriptionMap[userDescription])[0][1]
                    .map(value => <MenuItem value={value}>{value}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          <Typography variant="caption">
            {' '}
            By clicking Sign Up, you agree to our{' '}
            <Link href="https://airbtics.com/privacy-policy/terms-of-service/" target="_blank">
              Terms
            </Link>{' '}
            and that you have read our{' '}
            <Link href="https://airbtics.com/privacy-policy/privacy-policy/" target="_blank">
              Privacy Policy
            </Link>
            .{' '}
          </Typography>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={loading}
          >
            Sign Up
          </Button>
          <Grid container>
            <Grid item xs>
              <Link href="/reset-password" variant="body2">
                Forgot password?
              </Link>
            </Grid>
            <Grid item>
              {/* <Link onClick={redirectToLoginPage} variant="body2" style={{ cursor: 'pointer' }}>
                Already have account? Sign In
              </Link> */}
              <Copyright />
            </Grid>
          </Grid>
          {/* <Box mt={5}>
            
          </Box> */}
        </form>
      </div>
    </>
  );
};

export default Login;
